'use client';

import { useState, useMemo } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import { useMounted } from '@/hooks';

// Government Professional Color Palette + Soft Pastel Chart Colors
const COLORS = {
  primary: '#1A237E', // Deep Navy Blue
  accent: '#1B5E20', // Forest Green
  highlight: '#B8860B', // Rich Gold
  background: '#E3F2FD', // Light Sky Blue
  cardBg: '#FFFFFF', // White
  textPrimary: '#1A237E',
  textSecondary: '#455A64',
  textDark: '#2C3E50', // Dark grey for labels
  border: '#90CAF9',
  gradient1: '#1A237E',
  gradient2: '#64B5F6',
};

// Dark Pastel Colors for Charts (Richer, More Saturated)
const PASTEL_COLORS = [
  '#6ECFA0', // Dark Mint Green
  '#7BA3D4', // Deep Sky Blue
  '#9B7FC8', // Rich Lavender
  '#D97878', // Dark Coral
  '#D4A574', // Rich Amber
  '#9B7FB8', // Deep Purple
  '#5BA88B', // Dark Mint variant
  '#C4936D', // Deep Peach
  '#B87BA3', // Deep Lavender variant
  '#6BA85A', // Dark Green
];

// Sample data structure
const riceData = {
  basmatiCountries: [
    { name: 'Iran', percentage: 20.4, volume: 7344000, value: 2500 },
    { name: 'Saudi Arabia', percentage: 17.9, volume: 6444000, value: 2300 },
    { name: 'Iraq', percentage: 13.0, volume: 4680000, value: 1600 },
    { name: 'United Arab Emirates', percentage: 10.5, volume: 3783000, value: 1400 },
    { name: 'Kuwait', percentage: 8.2, volume: 2954000, value: 1100 },
    { name: 'Oman', percentage: 6.8, volume: 2449000, value: 900 },
    { name: 'Qatar', percentage: 5.4, volume: 1943000, value: 750 },
    { name: 'Bahrain', percentage: 4.1, volume: 1477000, value: 550 },
    { name: 'Mauritius', percentage: 3.2, volume: 1153000, value: 420 },
    { name: 'Indonesia', percentage: 2.8, volume: 1009000, value: 380 },
  ],
  nonBasmatiCountries: [
    { name: 'Bangladesh', percentage: 15.1, volume: 11919000, value: 2800 },
    { name: 'Liberia', percentage: 11.6, volume: 9152000, value: 1900 },
    { name: 'Côte d\'Ivoire', percentage: 10.9, volume: 8603000, value: 1800 },
    { name: 'Mali', percentage: 9.2, volume: 7259000, value: 1500 },
    { name: 'Nigeria', percentage: 8.7, volume: 6867000, value: 1400 },
    { name: 'Senegal', percentage: 7.4, volume: 5838000, value: 1200 },
    { name: 'Guinea', percentage: 6.3, volume: 4972000, value: 1000 },
    { name: 'Sierra Leone', percentage: 5.8, volume: 4578000, value: 920 },
    { name: 'Ghana', percentage: 4.9, volume: 3865000, value: 780 },
    { name: 'Sri Lanka', percentage: 4.2, volume: 3317000, value: 670 },
  ],
  yearlyData: [
    { year: 2015, basmati: 25000, nonBasmati: 65000, total: 90000 },
    { year: 2016, basmati: 27500, nonBasmati: 68000, total: 95500 },
    { year: 2017, basmati: 29000, nonBasmati: 70500, total: 99500 },
    { year: 2018, basmati: 31200, nonBasmati: 72000, total: 103200 },
    { year: 2019, basmati: 32800, nonBasmati: 75500, total: 108300 },
    { year: 2020, basmati: 30500, nonBasmati: 71000, total: 101500 },
    { year: 2021, basmati: 33000, nonBasmati: 77000, total: 110000 },
    { year: 2022, basmati: 35100, nonBasmati: 79500, total: 114600 },
    { year: 2023, basmati: 34500, nonBasmati: 81000, total: 115500 },
    { year: 2024, basmati: 35800, nonBasmati: 80500, total: 116300 },
    { year: 2025, basmati: 36000, nonBasmati: 78800, total: 114800 },
  ],
};

// Calculate CAGR
function calculateCAGR(startValue: number, endValue: number, years: number): number {
  return ((Math.pow(endValue / startValue, 1 / years) - 1) * 100);
}

// Custom Tooltip Component
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border-2 rounded-lg p-3" style={{ borderColor: COLORS.primary }}>
        <p className="font-serif font-semibold" style={{ color: COLORS.primary }}>
          {payload[0].payload.name || payload[0].payload.year}
        </p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color }} className="text-sm font-medium">
            {entry.name}: {entry.value?.toLocaleString() || entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Glassmorphic Card Component
const GlassmorphicCard = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div
    className={`rounded-2xl backdrop-blur-md bg-white/90 shadow-lg border border-white/20 p-6 hover:shadow-2xl transition-all duration-300 ${className}`}
    style={{
      background: 'rgba(255, 255, 255, 0.95)',
      border: `1px solid ${COLORS.border}`,
    }}
  >
    {children}
  </div>
);

// KPI Card Component
const KPICard = ({ label, value, unit, icon, accent = false }: any) => (
  <GlassmorphicCard className="text-center hover:scale-105">
    <div className="text-3xl mb-2">{icon}</div>
    <p className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>
      {label}
    </p>
    <p className="text-3xl font-bold font-serif mt-2" style={{ color: accent ? COLORS.highlight : COLORS.primary }}>
      {value}
    </p>
    <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
      {unit}
    </p>
  </GlassmorphicCard>
);

export default function DashboardPage() {
  const mounted = useMounted();
  const [activeTab, setActiveTab] = useState('basmati');

  const basmatiCAGR = useMemo(() => {
    const startValue = riceData.yearlyData[0].basmati;
    const endValue = riceData.yearlyData[riceData.yearlyData.length - 1].basmati;
    return calculateCAGR(startValue, endValue, 10);
  }, []);

  const nonBasmatiCAGR = useMemo(() => {
    const startValue = riceData.yearlyData[0].nonBasmati;
    const endValue = riceData.yearlyData[riceData.yearlyData.length - 1].nonBasmati;
    return calculateCAGR(startValue, endValue, 10);
  }, []);

  if (!mounted) return null;

  const chartData = activeTab === 'basmati' ? riceData.basmatiCountries : riceData.nonBasmatiCountries;
  const gradientId = `${activeTab}Gradient`;

  return (
    <div
      className="min-h-screen p-8 font-serif"
      style={{
        background: `linear-gradient(135deg, ${COLORS.background} 0%, #ffffff 50%, #E1F5FE 100%)`,
      }}
    >
      {/* Rice Plant Watermark */}
      <div
        className="fixed bottom-0 right-0 opacity-10 pointer-events-none"
        style={{ width: '400px', height: '400px' }}
      >
        <svg viewBox="0 0 200 400" className="w-full h-full">
          {/* Rice stalk */}
          <line x1="100" y1="50" x2="100" y2="350" stroke={COLORS.accent} strokeWidth="3" />
          {/* Leaves */}
          <ellipse cx="85" cy="100" rx="12" ry="25" fill={COLORS.accent} opacity="0.6" />
          <ellipse cx="115" cy="120" rx="12" ry="25" fill={COLORS.accent} opacity="0.6" transform="rotate(30 115 120)" />
          <ellipse cx="85" cy="160" rx="12" ry="25" fill={COLORS.accent} opacity="0.6" />
          <ellipse cx="115" cy="180" rx="12" ry="25" fill={COLORS.accent} opacity="0.6" transform="rotate(30 115 180)" />
          <ellipse cx="85" cy="220" rx="12" ry="25" fill={COLORS.accent} opacity="0.6" />
          <ellipse cx="115" cy="240" rx="12" ry="25" fill={COLORS.accent} opacity="0.6" transform="rotate(30 115 240)" />
          {/* Rice head */}
          <g transform="translate(100, 40)">
            <ellipse cx="0" cy="0" rx="8" ry="15" fill={COLORS.highlight} opacity="0.8" />
            <circle cx="-5" cy="-3" r="3" fill={COLORS.highlight} />
            <circle cx="5" cy="-2" r="3" fill={COLORS.highlight} />
            <circle cx="0" cy="8" r="3" fill={COLORS.highlight} />
          </g>
        </svg>
      </div>

      {/* Custom Cursor CSS */}
      <style>{`
        * {
          cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="%231B5E20" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/></svg>') 12 12, auto;
        }
        
        button, a, [role="button"], .cursor-pointer {
          cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" fill="%231B5E20" opacity="0.7"/><path fill="%231B5E20" d="M12 4c-1.1 0-2 .9-2 2v4H6c-1.1 0-2 .9-2 2s.9 2 2 2h4v4c0 1.1.9 2 2 2s2-.9 2-2v-4h4c1.1 0 2-.9 2-2s-.9-2-2-2h-4V6c0-1.1-.9-2-2-2z"/></svg>') 12 12, pointer;
        }
        
        .transition-all {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-5xl font-bold mb-2" style={{ color: COLORS.primary }}>
            India's Basmati Rice Trade
          </h1>
          <div className="h-1 w-32 mx-auto mt-4 rounded-full" style={{ background: `linear-gradient(90deg, ${COLORS.primary} 0%, ${COLORS.highlight} 100%)` }} />
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <KPICard
            label="Total Annual Export"
            value="114.8M"
            unit="Metric Tonnes"
            icon="🌾"
          />
          <KPICard
            label="Basmati Export"
            value="36.0M"
            unit="MT (31.4%)"
            icon="🍚"
          />
          <KPICard
            label="Non-Basmati Export"
            value="78.8M"
            unit="MT (68.6%)"
            icon="🌾"
          />
          <KPICard
            label="Export CAGR"
            value="4.2%"
            unit="10-Year Growth"
            icon="📈"
            accent
          />
        </div>

        {/* Tabs for switching between Basmati and Non-Basmati */}
        <div className="flex gap-4 mb-8 justify-center">
          <button
            onClick={() => setActiveTab('basmati')}
            className="px-6 py-2 rounded-lg font-semibold transition-all"
            style={{
              background: activeTab === 'basmati' ? COLORS.primary : 'transparent',
              color: activeTab === 'basmati' ? '#fff' : COLORS.primary,
              border: `2px solid ${COLORS.primary}`,
            }}
          >
            Basmati Rice
          </button>
          <button
            onClick={() => setActiveTab('nonBasmati')}
            className="px-6 py-2 rounded-lg font-semibold transition-all"
            style={{
              background: activeTab === 'nonBasmati' ? COLORS.primary : 'transparent',
              color: activeTab === 'nonBasmati' ? '#fff' : COLORS.primary,
              border: `2px solid ${COLORS.primary}`,
            }}
          >
            Non-Basmati Rice
          </button>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-2 gap-6 mb-8">
          {/* Top 10 Countries Bar Chart */}
          <GlassmorphicCard>
            <h2 className="text-2xl font-bold mb-6" style={{ color: COLORS.primary }}>
              Top 10 Destination Countries
            </h2>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  height={100} 
                  tick={{ fontSize: 12, fill: COLORS.textDark, fontWeight: 500 }} 
                />
                <YAxis tick={{ fill: COLORS.textSecondary }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="volume" radius={[8, 8, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PASTEL_COLORS[index % PASTEL_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </GlassmorphicCard>

          {/* Market Share Pie Chart */}
          <GlassmorphicCard>
            <h2 className="text-2xl font-bold mb-6" style={{ color: COLORS.primary }}>
              Market Distribution
            </h2>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={chartData.slice(0, 5)}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }: any) => `${name} (${value}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="percentage"
                >
                  {PASTEL_COLORS.slice(0, 5).map((color, index) => (
                    <Cell key={`cell-${index}`} fill={color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value}%`} />
              </PieChart>
            </ResponsiveContainer>
          </GlassmorphicCard>
        </div>

        {/* 11-Year Trend Analysis */}
        <GlassmorphicCard className="mb-8">
          <h2 className="text-2xl font-bold mb-6" style={{ color: COLORS.primary }}>
            11-Year Export Trends (2015-2025)
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={riceData.yearlyData}>
              <defs>
                <linearGradient id="basmatiLine" x1="0" y1="0" x2="100%" y2="0">
                  <stop offset="0%" stopColor={COLORS.primary} />
                  <stop offset="100%" stopColor={COLORS.accent} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ paddingTop: '20px' }} />
              <Line
                type="monotone"
                dataKey="basmati"
                stroke={COLORS.primary}
                strokeWidth={3}
                dot={{ fill: COLORS.primary, r: 5 }}
                activeDot={{ r: 7 }}
              />
              <Line
                type="monotone"
                dataKey="nonBasmati"
                stroke={COLORS.accent}
                strokeWidth={3}
                dot={{ fill: COLORS.accent, r: 5 }}
                activeDot={{ r: 7 }}
              />
              <Line
                type="monotone"
                dataKey="total"
                stroke={COLORS.highlight}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </GlassmorphicCard>

        {/* CAGR Analysis Table */}
        <GlassmorphicCard className="mb-8">
          <h2 className="text-2xl font-bold mb-6" style={{ color: COLORS.primary }}>
            Compound Annual Growth Rate Analysis
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `2px solid ${COLORS.primary}` }}>
                  <th className="text-left py-3 px-4 font-bold" style={{ color: COLORS.primary }}>
                    Category
                  </th>
                  <th className="text-right py-3 px-4 font-bold" style={{ color: COLORS.primary }}>
                    2015 (MT)
                  </th>
                  <th className="text-right py-3 px-4 font-bold" style={{ color: COLORS.primary }}>
                    2025 (MT)
                  </th>
                  <th className="text-right py-3 px-4 font-bold" style={{ color: COLORS.highlight }}>
                    CAGR %
                  </th>
                  <th className="text-right py-3 px-4 font-bold" style={{ color: COLORS.accent }}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td className="py-3 px-4" style={{ color: COLORS.textPrimary }}>
                    Basmati Rice
                  </td>
                  <td className="py-3 px-4 text-right" style={{ color: COLORS.textSecondary }}>
                    25,000,000
                  </td>
                  <td className="py-3 px-4 text-right" style={{ color: COLORS.textSecondary }}>
                    36,000,000
                  </td>
                  <td className="py-3 px-4 text-right font-bold" style={{ color: COLORS.highlight }}>
                    {basmatiCAGR.toFixed(2)}%
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="px-3 py-1 rounded-full text-sm font-semibold" style={{ background: '#C8E6C9', color: COLORS.accent }}>
                      ↑ Growing
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4" style={{ color: COLORS.textPrimary }}>
                    Non-Basmati Rice
                  </td>
                  <td className="py-3 px-4 text-right" style={{ color: COLORS.textSecondary }}>
                    65,000,000
                  </td>
                  <td className="py-3 px-4 text-right" style={{ color: COLORS.textSecondary }}>
                    78,800,000
                  </td>
                  <td className="py-3 px-4 text-right font-bold" style={{ color: COLORS.highlight }}>
                    {nonBasmatiCAGR.toFixed(2)}%
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="px-3 py-1 rounded-full text-sm font-semibold" style={{ background: '#C8E6C9', color: COLORS.accent }}>
                      ↑ Growing
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </GlassmorphicCard>

        {/* Current Market Scenario Analysis */}
        <GlassmorphicCard className="mb-8">
          <h2 className="text-2xl font-bold mb-6" style={{ color: COLORS.primary }}>
            Current Market Scenario Analysis
          </h2>
          <div className="space-y-4">
            <div className="p-4 rounded-lg" style={{ background: '#FFF9E6', borderLeft: `4px solid ${COLORS.highlight}` }}>
              <h3 className="font-bold mb-2" style={{ color: COLORS.primary }}>
                Middle East Concentration Risk
              </h3>
              <p style={{ color: COLORS.textSecondary }}>
                Current market analysis reveals strong concentration in the Middle East region, with Iran (20.4%), Saudi Arabia (17.9%), and Iraq (13.0%) comprising <span className="font-bold">51.3% of Basmati rice exports</span>. While this represents significant revenue opportunity, geopolitical volatility and supply chain disruptions in 2026 pose substantial risks to portfolio sustainability.
              </p>
            </div>

            <div className="p-4 rounded-lg" style={{ background: '#E6F7FF', borderLeft: `4px solid #B4D7FF` }}>
              <h3 className="font-bold mb-2" style={{ color: COLORS.primary }}>
                Geopolitical Risk Assessment
              </h3>
              <ul className="space-y-2" style={{ color: COLORS.textSecondary }}>
                <li><strong>Iran:</strong> Export sanctions and currency volatility create unpredictable trading conditions</li>
                <li><strong>Iraq:</strong> Political instability and infrastructure challenges may disrupt supply chains</li>
                <li><strong>Kuwait:</strong> Regional tensions and potential trade restrictions pose medium-term risk</li>
              </ul>
            </div>

            <div className="p-4 rounded-lg" style={{ background: '#E6F5FF', borderLeft: `4px solid #90CAF9` }}>
              <h3 className="font-bold mb-2" style={{ color: COLORS.primary }}>
                Strategic Recommendation
              </h3>
              <p style={{ color: COLORS.textSecondary }}>
                Diversification into stable, growing Southeast Asian markets will reduce portfolio concentration risk while capturing emerging demand from high-GDP-growth economies.
              </p>
            </div>
          </div>
        </GlassmorphicCard>

        {/* Strategic Expansion Targets */}
        <GlassmorphicCard className="mb-8">
          <h2 className="text-2xl font-bold mb-6" style={{ color: COLORS.primary }}>
            Strategic Expansion Targets: Next Steps
          </h2>
          <div className="grid grid-cols-3 gap-6">
            {/* Target 1: Vietnam */}
            <div className="p-5 rounded-lg border-2" style={{ borderColor: '#A8E6CF', background: '#F5FFFA' }}>
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold" style={{ background: '#A8E6CF', color: '#1B5E20' }}>
                  1
                </div>
                <h3 className="text-lg font-bold ml-3" style={{ color: COLORS.primary }}>
                  Vietnam
                </h3>
              </div>
              <p className="text-sm font-semibold mb-2" style={{ color: COLORS.textSecondary }}>
                🚀 High-Potential Market
              </p>
              <p style={{ color: COLORS.textSecondary, fontSize: '14px', lineHeight: '1.6' }}>
                <strong>Why Target Vietnam?</strong><br/>
                • GDP growth rate: 6.5%+ YoY (2026)<br/>
                • Rising middle-class consumption increasing rice demand<br/>
                • Strategic trade partnerships with India under CPTPP<br/>
                • Growing preference for premium Basmati varieties among urban consumers
              </p>
              <div className="mt-3 p-2 rounded" style={{ background: '#E0F7E0' }}>
                <p style={{ color: '#1B5E20', fontSize: '12px', fontWeight: 'bold' }}>
                  Potential Volume: 500K–800K MT annually
                </p>
              </div>
            </div>

            {/* Target 2: Indonesia */}
            <div className="p-5 rounded-lg border-2" style={{ borderColor: '#B4D7FF', background: '#F5F9FF' }}>
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold" style={{ background: '#B4D7FF', color: '#1A237E' }}>
                  2
                </div>
                <h3 className="text-lg font-bold ml-3" style={{ color: COLORS.primary }}>
                  Indonesia
                </h3>
              </div>
              <p className="text-sm font-semibold mb-2" style={{ color: COLORS.textSecondary }}>
                🌏 Largest Southeast Asian Economy
              </p>
              <p style={{ color: COLORS.textSecondary, fontSize: '14px', lineHeight: '1.6' }}>
                <strong>Why Target Indonesia?</strong><br/>
                • Population 270M+ with growing consumer sophistication<br/>
                • Infrastructure investments improving port connectivity<br/>
                • Regional trade hub positioning for SE Asian distribution<br/>
                • Emerging premium food segment targeting affluent urban demographics
              </p>
              <div className="mt-3 p-2 rounded" style={{ background: '#E0EFFF' }}>
                <p style={{ color: '#1A237E', fontSize: '12px', fontWeight: 'bold' }}>
                  Potential Volume: 600K–1M MT annually
                </p>
              </div>
            </div>

            {/* Target 3: Philippines */}
            <div className="p-5 rounded-lg border-2" style={{ borderColor: '#D4C5F9', background: '#F8F5FF' }}>
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold" style={{ background: '#D4C5F9', color: '#4A148C' }}>
                  3
                </div>
                <h3 className="text-lg font-bold ml-3" style={{ color: COLORS.primary }}>
                  Philippines
                </h3>
              </div>
              <p className="text-sm font-semibold mb-2" style={{ color: COLORS.textSecondary }}>
                💼 Emerging High-Growth Market
              </p>
              <p style={{ color: COLORS.textSecondary, fontSize: '14px', lineHeight: '1.6' }}>
                <strong>Why Target Philippines?</strong><br/>
                • GDP growth: 6.0%+ with rising disposable incomes<br/>
                • Rapid urbanization driving premium food consumption<br/>
                • Low current Basmati penetration = expansion room<br/>
                • Growing hospitality and fine-dining sector (2026 tourism boom)
              </p>
              <div className="mt-3 p-2 rounded" style={{ background: '#F0E8FF' }}>
                <p style={{ color: '#4A148C', fontSize: '12px', fontWeight: 'bold' }}>
                  Potential Volume: 300K–500K MT annually
                </p>
              </div>
            </div>
          </div>

          {/* Summary Actionable Insights */}
          <div className="mt-8 p-5 rounded-lg" style={{ background: '#F0F8FF', border: `2px solid ${COLORS.border}` }}>
            <h3 className="font-bold mb-4 text-lg" style={{ color: COLORS.primary }}>
              📊 Actionable Expansion Strategy
            </h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="font-semibold mb-2" style={{ color: COLORS.primary }}>
                  Phase 1: Market Entry (Q3-Q4 2026)
                </p>
                <ul style={{ color: COLORS.textSecondary, fontSize: '14px' }} className="space-y-1">
                  <li>✓ Establish trade partnerships in Vietnam (50K MT pilot)</li>
                  <li>✓ Negotiate distribution deals with Indonesian importers</li>
                  <li>✓ Initiate market research in Philippines retail sector</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2" style={{ color: COLORS.primary }}>
                  Phase 2: Scale-Up (2027)
                </p>
                <ul style={{ color: COLORS.textSecondary, fontSize: '14px' }} className="space-y-1">
                  <li>✓ Increase Vietnam volumes to 500K MT based on demand</li>
                  <li>✓ Establish regional logistics hub in Singapore/Bangkok</li>
                  <li>✓ Launch targeted marketing in Philippine premium segments</li>
                </ul>
              </div>
            </div>

            <div className="mt-4 p-3 rounded" style={{ background: '#E8F5E9', borderLeft: `4px solid #1B5E20` }}>
              <p style={{ color: '#1B5E20', fontSize: '13px', fontWeight: 'bold' }}>
                💡 Expected Impact: Reduce Middle East concentration from 51.3% to ~35% by end of 2027, adding 1.4M–2.3M MT annual capacity from Southeast Asia.
              </p>
            </div>
          </div>
        </GlassmorphicCard>

        {/* Top Exporters Table */}
        <GlassmorphicCard className="mb-12">
          <h2 className="text-2xl font-bold mb-6" style={{ color: COLORS.primary }}>
            Top Destinations - Detailed Analysis
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `2px solid ${COLORS.primary}` }}>
                  <th className="text-left py-3 px-4 font-bold" style={{ color: COLORS.primary }}>
                    Rank
                  </th>
                  <th className="text-left py-3 px-4 font-bold" style={{ color: COLORS.primary }}>
                    Country
                  </th>
                  <th className="text-right py-3 px-4 font-bold" style={{ color: COLORS.primary }}>
                    Volume (MT)
                  </th>
                  <th className="text-right py-3 px-4 font-bold" style={{ color: COLORS.primary }}>
                    Market Share %
                  </th>
                  <th className="text-right py-3 px-4 font-bold" style={{ color: COLORS.primary }}>
                    Value (Rs Cr)
                  </th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((country, index) => (
                  <tr key={index} style={{ borderBottom: `1px solid ${COLORS.border}` }} className="hover:bg-blue-50 transition-colors">
                    <td className="py-3 px-4 font-bold" style={{ color: COLORS.primary }}>
                      #{index + 1}
                    </td>
                    <td className="py-3 px-4" style={{ color: COLORS.textPrimary }}>
                      {country.name}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold" style={{ color: COLORS.textSecondary }}>
                      {(country.volume / 1000000).toFixed(2)}M
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="px-3 py-1 rounded-full text-sm font-semibold" style={{ background: '#E3F2FD', color: COLORS.primary }}>
                        {country.percentage}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-semibold" style={{ color: COLORS.highlight }}>
                      ₹{country.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassmorphicCard>

        {/* Footer */}
        <div className="text-center py-8 mt-12 border-t-2" style={{ borderColor: COLORS.border }}>
        </div>
      </div>
    </div>
  );
}
